import React from 'react'
import { Outlet } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/Button'
import { NfcRwaLogo } from '@/components/branding/NfcRwaLogo'

// Utility function to scroll to top
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  })
}

export const PublicLayout: React.FC = () => {
  const { isAuthenticated } = useAuthStore()

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <NfcRwaLogo className="h-8 w-8 sm:h-10 sm:w-10 shrink-0" />
                <span className="text-sm sm:text-lg lg:text-xl font-bold text-gray-900 hidden xs:block">
                  NFCRWA- NEW FRIENDS COLONY RESIDENTS WELFARE ASSOCIATION
                </span>
                <span className="text-sm sm:text-lg font-bold text-gray-900 xs:hidden">
                  NFCRWA
                </span>
              </Link>
            </div>

            {/* Navigation - Removed as requested */}

            {/* Action Buttons */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {isAuthenticated ? (
                <Link to="/app/dashboard" onClick={scrollToTop}>
                  <Button variant="primary" className="text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2">
                    <span className="hidden sm:inline">Go to Dashboard</span>
                    <span className="sm:hidden">Dashboard</span>
                  </Button>
                </Link>
              ) : (
                <a
                  href="https://wa.me/919810375098"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 text-white px-3 sm:px-6 py-1.5 sm:py-2 rounded-lg hover:bg-green-700 transition-colors font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  <span className="hidden sm:inline">Let's Talk</span>
                  <span className="sm:hidden">Talk</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Company Info */}
            <div className="sm:col-span-2 lg:col-span-1">
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">NFCRWA- NEW FRIENDS COLONY RESIDENTS WELFARE ASSOCIATION</h3>
              <p className="text-sm sm:text-base text-gray-300 mb-4">
                Dedicated to providing excellent community management services and fostering a harmonious living environment for all residents.
              </p>
              <div className="flex space-x-3 sm:space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <span className="sr-only">Facebook</span>
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <span className="sr-only">Twitter</span>
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <span className="sr-only">WhatsApp</span>
                  <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link 
                    to="/about" 
                    className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors"
                    onClick={scrollToTop}
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/contact" 
                    className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors"
                    onClick={scrollToTop}
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/complaint" 
                    className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors"
                    onClick={scrollToTop}
                  >
                    Register Complaint
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/enquiry" 
                    className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors"
                    onClick={scrollToTop}
                  >
                    General Enquiry
                  </Link>
                </li>
              </ul>
            </div>

            {/* Member Access */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Member Access</h3>
              <ul className="space-y-2">
                <li>
                  <Link 
                    to="/login" 
                    className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors"
                    onClick={scrollToTop}
                  >
                    Member Login
                  </Link>
                </li>
                <li>
                  <Link 
                    to="/user-login" 
                    className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors"
                    onClick={scrollToTop}
                  >
                    Login
                  </Link>
                </li>
                <li>
                  <a 
                    href="/nfcrwacontrol" 
                    className="text-sm sm:text-base text-gray-300 hover:text-white transition-colors"
                    onClick={scrollToTop}
                  >
                    Admin Control
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Contact Info</h3>
              <ul className="space-y-2 text-sm sm:text-base text-gray-300">
                <li className="break-words">Email: office@nfcrwa.com</li>
                <li>Phone: +91 9810375098</li>
                <li className="break-words">Address: OPP A -297 ASSOCIATION ASHOKA PARK ROAD NEW FRIENDS COLONY NEW DELHI 110065</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-700 text-center text-gray-400">
            <p className="text-xs sm:text-sm">&copy; 2024 NFCRWA- NEW FRIENDS COLONY RESIDENTS WELFARE ASSOCIATION. All rights reserved.</p>
            <p className="mt-1 text-xs sm:text-sm">Powered by Ameera IT Corporate Services</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default PublicLayout
